/**
 * Add a record to a master.
 *
 * `?parent=<code>` is set when arriving from a tree's "Inside X" action, so the
 * new node is pre-attached to the branch the user clicked.
 */

import { useParams, useSearchParams } from "react-router-dom";

import { Crumbs, PageHead, Problem } from "@/components/kit";
import { RecordFormClient } from "@/components/masters/RecordFormClient";
import { displayName } from "@/lib/format";
import { loadMaster, loadRelatedRows } from "@/lib/masters/load";
import { useApi } from "@/spa/useApi";

export function NewRecordPage() {
  const { sport = "", master: masterCode = "" } = useParams<{ sport: string; master: string }>();
  const [search] = useSearchParams();
  const parentCode = search.get("parent") ?? undefined;

  const { data, loading, error } = useApi(
    async () => {
      const master = await loadMaster(sport, masterCode);
      const related = await loadRelatedRows(sport, master);
      return { master, related };
    },
    [sport, masterCode],
    "failed to load",
  );

  if (error) {
    return (
      <Problem>
        Could not load {masterCode}: {error}
      </Problem>
    );
  }
  if (!data) return <PageHead title="New record" note={loading ? "Loading…" : ""} />;

  const { master, related } = data;
  return (
    <>
      <Crumbs
        trail={[
          { label: "Sports", href: "/sports" },
          { label: displayName(sport), href: `/${sport}` },
          { label: master.label, href: `/${sport}/masters/${masterCode}` },
          { label: "New" },
        ]}
      />
      <PageHead
        title={`New ${master.label.toLowerCase()}`}
        note="Every field below comes from this sport's own definition, in the order it was declared."
      />
      <RecordFormClient sport={sport} master={master} related={related} parentCode={parentCode} />
    </>
  );
}
